import React, { useState, useRef, useEffect, useCallback } from 'react';
import './LocationSearch.css';

const LocationSearch = ({
  value,
  onChange,
  label = 'Location',
  placeholder = 'Search for a city or country...',
  required = false,
  error = ''
}) => {
  const [inputValue, setInputValue] = useState(value?.formatted || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search function
  const searchLocations = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);

    try {
      // Using Nominatim API (OpenStreetMap) - free, no API key needed
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}&` +
        `format=json&` +
        `addressdetails=1&` +
        `limit=10`,
        {
          headers: {
            'Accept-Language': 'en',
            'User-Agent': 'MovenMuse/1.0'
          }
        }
      );

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();

      // Filter and format results - allow cities and countries
      const locationResults = data
        .filter(item =>
          item.type === 'city' ||
          item.type === 'town' ||
          item.type === 'village' ||
          item.type === 'country' ||
          item.type === 'state' ||
          item.type === 'administrative' ||
          item.addresstype === 'city' ||
          item.addresstype === 'town' ||
          item.addresstype === 'village' ||
          item.addresstype === 'country' ||
          item.class === 'place' ||
          item.class === 'boundary'
        )
        .map(item => {
          const city = item.address?.city ||
                       item.address?.town ||
                       item.address?.village ||
                       item.address?.state ||
                       '';
          const country = item.address?.country || '';

          // Determine formatted string
          let formatted = '';
          if (item.type === 'country') {
            formatted = country || item.name;
          } else if (city && country) {
            formatted = `${city}, ${country}`;
          } else if (country) {
            formatted = country;
          } else {
            formatted = item.name || item.display_name.split(',')[0];
          }

          return {
            id: item.place_id,
            city: city,
            country: country,
            formatted: formatted,
            display_name: item.display_name,
            type: item.type
          };
        })
        .filter((item, index, self) =>
          // Remove duplicates based on formatted name
          index === self.findIndex(t => t.formatted === item.formatted)
        );

      setSuggestions(locationResults);
      setShowDropdown(locationResults.length > 0);
      setHighlightedIndex(-1);
    } catch (err) {
      console.error('Location search error:', err);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce search by 300ms
    debounceRef.current = setTimeout(() => {
      searchLocations(newValue);
    }, 300);

    // Clear the structured value while typing
    if (value?.formatted !== newValue) {
      onChange({
        city: '',
        country: '',
        formatted: ''
      });
    }
  };

  // Handle selection from dropdown
  const handleSelect = (suggestion) => {
    setInputValue(suggestion.formatted);
    setSuggestions([]);
    setShowDropdown(false);

    onChange({
      city: suggestion.city || '',
      country: suggestion.country || '',
      formatted: suggestion.formatted
    });
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Handle clear
  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);
    onChange({
      city: '',
      country: '',
      formatted: ''
    });
    inputRef.current?.focus();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !inputRef.current?.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync with external value changes
  useEffect(() => {
    if (value?.formatted) {
      setInputValue(value.formatted);
    }
  }, [value]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Get icon based on location type
  const getLocationIcon = (type) => {
    if (type === 'country') {
      return 'bi-globe';
    }
    return 'bi-geo-alt-fill';
  };

  return (
    <div className="location-search">
      {label && (
        <label className="location-label">
          {label}
          {required && <span className="required-asterisk">*</span>}
        </label>
      )}

      <div className={`location-input-container ${error ? 'has-error' : ''}`}>
        <i className="bi bi-geo-alt location-icon"></i>
        <input
          ref={inputRef}
          type="text"
          className="location-input"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          autoComplete="off"
        />
        {isLoading && (
          <div className="loading-indicator">
            <div className="spinner-small"></div>
          </div>
        )}
        {inputValue && !isLoading && (
          <button
            type="button"
            className="clear-btn"
            onClick={handleClear}
            title="Clear"
          >
            <i className="bi bi-x"></i>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div className="location-dropdown" ref={dropdownRef}>
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              className={`location-dropdown-item ${index === highlightedIndex ? 'highlighted' : ''}`}
              onClick={() => handleSelect(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <i className={`bi ${getLocationIcon(suggestion.type)}`}></i>
              <span>{suggestion.formatted}</span>
            </div>
          ))}
          <div className="osm-attribution">
            Data © OpenStreetMap contributors
          </div>
        </div>
      )}

      {error && <div className="location-error">{error}</div>}

      <p className="location-hint">
        <i className="bi bi-info-circle"></i>
        Select a city or country from the suggestions
      </p>
    </div>
  );
};

export default LocationSearch;
